import { Box, Typography } from '@mui/material'
import { RouteProps } from 'routes/AppRouter'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { useTranslation } from 'react-i18next'
import React, { ChangeEvent, FormEvent, useState, useEffect } from 'react'
import { navigate } from '@reach/router'
import { ROUTE_TRAININGS } from '../../routes/routes-constants'
import { getTrainingContainer } from '../../container/training-module'
import { ITRainingService, TRAINING_SERVICE_KEY } from '../../modules/training'
import { emptyTrainingDTO, TrainingDTO } from '../../modules/training/models/TrainingDTO'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from '../../common/status/StatusService'
import { getUserContainer } from '../../container/user-module'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { Input, InputTextDescription } from '../resource/userProfileStylesMui'
import styles from './Editor.module.css'
import { PageLayout } from 'pages/layout'

const trainingContainer = getTrainingContainer()
const trainingService = trainingContainer.get<ITRainingService>(TRAINING_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)
const userContainer = getUserContainer()
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function Editor(props: RouteProps) {
  const { t } = useTranslation()
  const [training, setTraining] = useState<TrainingDTO>(emptyTrainingDTO())
  const [isLoading, setIsLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({
    title: false,
    description: false,
    image: false,
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target
    setTraining((prev) => ({
      ...prev,
      [id]: value,
    }))

    if (fieldErrors[id as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({
        ...prev,
        [id]: false,
      }))
    }
  }

  const handleBack = () => navigate(ROUTE_TRAININGS)

  const validateForm = (): boolean => {
    const errors = {
      title: false,
      description: false,
      image: false,
    }
    let isValid = true

    if (!training.title || training.title.trim() === '') {
      errors.title = true
      isValid = false
      statusService.sendStatus({ variant: 'warning', message: t('trainingTitleRequired') })
    }

    if (!training.description || training.description.trim() === '') {
      errors.description = true
      isValid = false
      statusService.sendStatus({ variant: 'warning', message: t('trainingDescriptionRequired') })
    }

    if (training.image && training.image.trim() !== '') {
      try {
        new URL(training.image)
      } catch {
        errors.image = true
        isValid = false
        statusService.sendStatus({ variant: 'warning', message: t('invalidImageUrl') })
      }
    }

    setFieldErrors(errors)
    return isValid
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const loggedUser = loggedUserService.get()
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    const creatorID = training.creatorID || loggedUser?.id || ''

    if (!creatorID || creatorID.trim() === '') {
      setIsLoading(false)
      statusService.sendStatus({
        variant: 'error',
        message: t('creatorIDMissing') || 'Falta el ID del creador',
      })
      return
    }

    const trainingToSave: TrainingDTO = {
      ...training,
      title: training.title.trim(),
      description: training.description.trim(),
      image: training.image?.trim() || '',
      creatorID: creatorID,
    }

    if (props.id && props.id !== 'create') {
      trainingService.update(trainingToSave).subscribe((result) => {
        setIsLoading(false)
        if (result) {
          statusService.sendStatus({
            variant: 'success',
            message: t('trainingUpdatedSuccess'),
          })
          handleBack()
        }
      })
    } else {
      trainingService.add(trainingToSave).subscribe((result) => {
        setIsLoading(false)
        if (result) {
          statusService.sendStatus({
            variant: 'success',
            message: t('trainingCreatedSuccess'),
          })
          handleBack()
        }
      })
    }
  }

  useEffect(() => {
    if (props.id && props.id !== 'create') {
      setIsLoading(true)
      trainingService.getByID(props.id).subscribe((trainingData) => {
        setIsLoading(false)
        if (trainingData) {
          setTraining({
            id: trainingData.id,
            title: trainingData.title,
            description: trainingData.description,
            image: trainingData.image || '',
            creatorID: trainingData.creatorID,
          })
        }
      })
    } else {
      const loggedUser = loggedUserService.get()

      if (loggedUser?.id) {
        setTraining((prev) => ({
          ...prev,
          creatorID: loggedUser.id,
        }))
      }
    }
  }, [props.id])

  return (
    <PageLayout>
      <form onSubmit={handleSave} className={styles.form}>
        <Box mb={3} className={styles.inputBox}>
          <Typography className={styles.label}>{t('title')}</Typography>
          <Input
            fullWidth
            id="title"
            value={training.title}
            variant="outlined"
            onChange={handleChange}
            required={true}
            error={fieldErrors.title}
            helperText={fieldErrors.title ? t('trainingTitleRequired') : ''}
            sx={{ width: '100%', '& .MuiInputBase-root': { paddingLeft: 2 } }}
          />
        </Box>
        <Box mb={3} className={styles.inputBox}>
          <Typography className={styles.label}>{t('description')}</Typography>
          <InputTextDescription
            fullWidth
            id="description"
            value={training.description}
            variant="outlined"
            onChange={handleChange}
            multiline
            rows={6}
            required={true}
            error={fieldErrors.description}
            helperText={fieldErrors.description ? t('trainingDescriptionRequired') : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--green-primary)',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--green-primary)',
              },
            }}
          />
        </Box>
        <Box mb={3} className={styles.inputBox}>
          <Typography className={styles.label}>{t('imageUrl')}</Typography>
          <Input
            fullWidth
            id="image"
            value={training.image}
            variant="outlined"
            onChange={handleChange}
            required
            placeholder="https://ejemplo.com/imagen.jpg"
            error={fieldErrors.image}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--green-primary)',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--green-primary)',
              },
            }}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" className={styles.buttonsContainer}>
          <Box className={styles.buttonSpacing}>
            <AppButton
              theme={ButtonTheme.NewSecondary}
              type="button"
              label={t('cancel')}
              handler={handleBack}
            />
          </Box>
          <AppButton
            theme={ButtonTheme.NewPrimary}
            type="submit"
            label={props.id ? t('save') : t('add')}
            handler={() => {}}
          />
        </Box>
      </form>
    </PageLayout>
  )
}
